import { useCallback, useEffect, useRef, useState } from 'react';

import * as passkeyApi from 'darwin_core/api/passkeys';
import {
  describePasskey,
  formatPasskeyDate,
  MAX_PASSKEYS,
  needsSignIn,
  PASSKEY_ERRORS,
  passkeyApiProblem,
} from 'darwin_core/passkeys';

import SignOutButton from '../auth/SignOutButton.jsx';
import ModalShell from '../components/ModalShell.jsx';
import { PasskeyIcon } from '../components/icons.jsx';
import ScreenHeader from '../components/ScreenHeader.jsx';
import { ACCOUNT_PATH } from '../navigation/paths.js';
import {
  addPasskey,
  passkeyAlreadyAdded,
  passkeyCancelled,
  passkeysSupported,
} from '../passkeys/webauthn.js';

// The Passkeys page, under Account: the account's passkeys, each with when it
// was added and last used and a way to remove it, plus adding one in this
// browser.

const LOAD_FAILED = 'Your passkeys could not be loaded.';
const ADDED_BUT_LOAD_FAILED =
  'The passkey was added, but your passkeys could not be reloaded.';
const REMOVED_BUT_LOAD_FAILED =
  'The passkey was removed, but your passkeys could not be reloaded.';
const REMOVE_FAILED =
  'The passkey could not be removed. Please try again later.';

const SIGN_IN_AGAIN =
  'Your session has ended. Log out and log in again, then try again.';
const RESET_SINCE_SIGN_IN =
  'Your password was reset after you logged in here. Log out and log in again with your new password.';

const signInAgainMessage = (error) =>
  passkeyApiProblem(error) === 'signedInBeforeReset'
    ? RESET_SINCE_SIGN_IN
    : SIGN_IN_AGAIN;

// What to say when adding a passkey fails. A cancelled prompt is not a failure
// and never reaches here.
function addFailedMessage(error) {
  switch (passkeyApiProblem(error)) {
    case 'limitReached':
      return `You can have up to ${MAX_PASSKEYS} passkeys. Remove one you no longer use, then try again.`;
    case 'recentSignInRequired':
      return 'For your security, adding a passkey needs a recent sign-in. Log out, log in again, then add your passkey straight away.';
    case 'signedInBeforeReset':
    case 'sessionEnded':
      return signInAgainMessage(error);
    default:
      break;
  }
  if (passkeyAlreadyAdded(error)) {
    return 'This browser already has a passkey for your Halo account.';
  }
  return 'A passkey could not be added. Please try again later.';
}

// What the confirmation before removing `passkey` says. The app's says the
// same, of a phone.
function removeDescription(passkey) {
  const { title, inlineDetail, noun, provider, legacy } =
    describePasskey(passkey);
  const stopped = legacy
    ? `You won’t be able to sign in to Halo with “${title}” (${inlineDetail}) any more.`
    : `You won’t be able to sign in to Halo with the ${noun} (${inlineDetail}) any more.`;
  return `${stopped} A device already signed in with it stays signed in, and the passkey stays in ${
    provider ?? 'its password manager'
  } until you delete it there.`;
}

export default function PasskeysScreen() {
  const [supported] = useState(passkeysSupported);
  // null until the first load finishes
  const [passkeys, setPasskeys] = useState(null);
  // When a password reset last removed the account's passkeys, if none has
  // been added since.
  const [removedByResetAt, setRemovedByResetAt] = useState(null);
  // The latest load failed, so the list on hand can't be trusted: show the error
  // alone rather than an empty list (or one a removal has just changed).
  const [loadFailed, setLoadFailed] = useState(false);
  // 'add' or 'remove' while one is running; either disables the buttons.
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // The error can only be fixed by signing in again, so offer Log out with it.
  const [offerSignOut, setOfferSignOut] = useState(false);
  // Announces a removal that worked: the row just disappears otherwise.
  const [status, setStatus] = useState('');
  const [toRemove, setToRemove] = useState(null);
  // Where keyboard focus should go once the screen has re-rendered after an
  // action: 'add', 'retry', a passkey id (its Remove button) or 'heading'.
  // Each action replaces or disables the control that had focus, which drops
  // focus to <body>.
  const [focusTarget, setFocusTarget] = useState(null);
  // Held while an add or a removal runs, so only one runs at a time. `busy`
  // disables the buttons only once React re-renders, so a fast double-click
  // could otherwise start a second: a second add would abort the browser's
  // first passkey prompt and may be refused, a second removal deletes twice.
  // The app's Passkeys screen does the same.
  const actionRef = useRef(false);
  const retryRef = useRef(null);
  const addRef = useRef(null);
  const removeRefs = useRef(new Map());

  useEffect(() => {
    if (!focusTarget) {
      return;
    }
    setFocusTarget(null);
    // Leave focus alone if the user has already moved it somewhere.
    const active = document.activeElement;
    if (active && active !== document.body && !active.disabled) {
      return;
    }
    const target =
      focusTarget === 'retry'
        ? retryRef.current
        : focusTarget === 'add'
          ? addRef.current
          : focusTarget === 'heading'
            ? null
            : removeRefs.current.get(focusTarget);
    (target ?? document.getElementById('main-content'))?.focus();
  }, [focusTarget]);

  // Resolves to whether the list loaded.
  const load = useCallback(async () => {
    try {
      const listed = await passkeyApi.listPasskeys();
      setPasskeys(listed.passkeys);
      setRemovedByResetAt(listed.removedByPasswordResetAt);
      setLoadFailed(false);
      return true;
    } catch (ex) {
      setLoadFailed(true);
      setError(needsSignIn(ex) ? signInAgainMessage(ex) : LOAD_FAILED);
      setOfferSignOut(needsSignIn(ex));
      return false;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRetry() {
    setError(null);
    // The load offers Log out again if signing in is still what's needed.
    setOfferSignOut(false);
    setLoadFailed(false);
    setPasskeys(null);
    // Back to Try again if it fails again, otherwise to the top of the page.
    setFocusTarget((await load()) ? 'heading' : 'retry');
  }

  async function handleAdd() {
    if (actionRef.current) {
      return;
    }
    actionRef.current = true;
    setError(null);
    setOfferSignOut(false);
    setStatus('');
    setBusy('add');
    // Back to Add; at the limit it's gone, so the heading instead.
    let focus = 'add';
    try {
      await addPasskey();
      if (!(await load())) {
        // The add worked, so say so, or the user may add it again. One
        // message, as for a removal.
        setError(ADDED_BUT_LOAD_FAILED);
        focus = 'retry';
      }
    } catch (ex) {
      if (!passkeyCancelled(ex)) {
        setError(addFailedMessage(ex));
        setOfferSignOut(needsSignIn(ex));
      }
    } finally {
      actionRef.current = false;
      setBusy(null);
      setFocusTarget(focus);
    }
  }

  async function handleRemove() {
    const passkey = toRemove;
    if (actionRef.current || !passkey) {
      return;
    }
    actionRef.current = true;
    setToRemove(null);
    setError(null);
    setOfferSignOut(false);
    setStatus('');
    setBusy('remove');
    // Closing the confirm hands focus back to this row's Remove button, which
    // is disabled while the removal runs. Once it's done, focus goes to the top
    // of the page (the row is gone) or, if it failed, back to that button.
    try {
      try {
        await passkeyApi.deletePasskey(passkey.id);
      } catch (ex) {
        // Already removed, in another tab or on another device. It's gone
        // either way, so reload the list rather than report a failure that
        // every retry would repeat.
        if (ex?.code !== PASSKEY_ERRORS.NOT_FOUND) {
          throw ex;
        }
      }
      setFocusTarget('heading');
      if (await load()) {
        setStatus('Passkey removed.');
      } else {
        // One message: the assertive alert would cut off a polite "removed".
        setError(REMOVED_BUT_LOAD_FAILED);
        setFocusTarget('retry');
      }
    } catch (ex) {
      setError(needsSignIn(ex) ? signInAgainMessage(ex) : REMOVE_FAILED);
      setOfferSignOut(needsSignIn(ex));
      setFocusTarget(passkey.id);
    } finally {
      actionRef.current = false;
      setBusy(null);
    }
  }

  const atLimit = (passkeys?.length ?? 0) >= MAX_PASSKEYS;

  return (
    <main className="app-shell screen passkeys">
      <ScreenHeader
        title="Passkeys"
        sub="Sign in without your password"
        backTo={ACCOUNT_PATH}
        backLabel="Account"
      />
      <p className="passkeys__note">
        Use your fingerprint, face or device PIN to sign in to Halo instead of
        typing your password.
      </p>
      {supported ? null : (
        <p className="passkeys__note">
          Passkeys aren’t supported in this browser.
        </p>
      )}

      {/* Always present so assistive tech has the live region before an error
          lands; collapses while empty. */}
      <p className="form-error" role="alert" aria-live="assertive">
        {error}
      </p>
      {offerSignOut ? <SignOutButton /> : null}
      <p className="visually-hidden" role="status">
        {status}
      </p>

      {loadFailed ? (
        <button
          ref={retryRef}
          type="button"
          className="passkeys__add"
          onClick={handleRetry}
        >
          Try again
        </button>
      ) : passkeys === null ? (
        <p className="passkeys__note">Loading your passkeys…</p>
      ) : passkeys.length === 0 ? (
        removedByResetAt ? (
          // Said outright, so passkeys vanishing after a reset doesn't look
          // like a bug.
          <p className="passkeys__note">
            Your passkeys were removed when your password was reset on{' '}
            {formatPasskeyDate(removedByResetAt)}. Add a passkey again to sign
            in without your password.
          </p>
        ) : (
          <p className="passkeys__note">You don’t have any passkeys.</p>
        )
      ) : (
        <ul className="passkeys__list" aria-label="Your passkeys">
          {passkeys.map((passkey) => {
            const { title, detail, noun, legacy } = describePasskey(passkey);
            return (
              <li key={passkey.id} className="passkeys__row">
                {/* The same for every passkey: there's no icon for each
                    password manager. */}
                <span className="passkeys__icon" aria-hidden="true">
                  <PasskeyIcon />
                </span>
                <div className="passkeys__row-text">
                  <span className="passkeys__name">{title}</span>
                  <span
                    id={`passkey-use-${passkey.id}`}
                    className="passkeys__use"
                  >
                    {detail}
                  </span>
                </div>
                <button
                  type="button"
                  className="link-button"
                  aria-label={
                    legacy ? `Remove passkey ${title}` : `Remove ${noun}`
                  }
                  // Two can share a title (the same password manager, or the
                  // same browser); the second line tells them apart.
                  aria-describedby={`passkey-use-${passkey.id}`}
                  ref={(button) => {
                    if (button) {
                      removeRefs.current.set(passkey.id, button);
                    } else {
                      removeRefs.current.delete(passkey.id);
                    }
                  }}
                  disabled={busy !== null}
                  onClick={() => setToRemove(passkey)}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {supported && passkeys !== null && !loadFailed ? (
        atLimit ? (
          <p className="passkeys__note">
            You have {MAX_PASSKEYS} passkeys, the most an account can have.
            Remove one you no longer use to add another.
          </p>
        ) : (
          <button
            ref={addRef}
            type="button"
            className="passkeys__add"
            disabled={busy !== null}
            onClick={handleAdd}
          >
            {busy === 'add' ? 'Working…' : 'Add a passkey'}
          </button>
        )
      ) : null}

      {toRemove ? (
        <ModalShell
          labelledBy="passkey-remove-title"
          className="schedule-modal--confirm"
          onClose={() => setToRemove(null)}
        >
          <h2 id="passkey-remove-title" className="schedule-modal__title">
            Remove passkey?
          </h2>
          {/* Removing a passkey only stops new sign-ins with it. Once there's
              a way to sign out everywhere, link to it here for a device that
              shouldn't stay signed in. */}
          <p className="schedule-modal__desc">{removeDescription(toRemove)}</p>
          <div className="schedule-modal__actions">
            <button
              type="button"
              className="schedule-modal__cancel"
              onClick={() => setToRemove(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={handleRemove}
            >
              Remove
            </button>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
}
